// frontend/src/components/sync/SyncScreen.jsx
//
// The screen in front of the paywall while the merchant's price waits on the
// import (`components/entry/entryScreen.js` PRICE_IMPORT): they have no
// access, and their price is read off the order history this import brings
// in. Every other import runs behind the app, on the sidebar's
// `SyncProgressPill`.
//
// Reads the `GET /api/store/sync-status/` payload
// (backend/shopify_app/services/sync_status.py). Two levels of copy,
// deliberately not the same sentence: the title bar names the wait ("Setting
// your plan"), the heading under it names the state ("Importing your data",
// "Loading your sales history", "Import failed"). The body says the plan
// follows by itself, and a completed run still waits: its orders are
// landing, and the price with them.
//
// Phases: Locations → Products → Inventory (`utils/syncGate.CATALOG_PHASES`
// and `phaseState`). A failed import whose phases all got through says the
// catalog is in and the order history did not finish
// (`error.orderHistoryBody`).
//
// A ROW COUNTS THE WORK, NOT ONLY THE CHANGES. A refresh of a store whose
// data is current changes nothing, and a restarted import skips the records
// it already wrote, so `synced` alone reads as a sync doing nothing. Every row
// prints the records the run visited (`utils/syncGate.visitedCount`); a
// refresh row says so as "Checked N", then "all up to date" or the `synced`
// count as "N updated".
//
// A failed run says what it asks of the merchant (`utils/syncGate.failureState`
// off `sync.failureReason`): reconnect the store (a Reconnect button to
// `reconnectUrl`, the shop's sign-in door), wait for Shopify (the import starts
// again by itself), or the plain failure with Contact support (the in-app
// support form).
//
// The payload is read as sent. The entry order only shows this screen for
// a run that exists, and `sync_status_payload` sets every counter on every
// run, so a missing field is a broken contract — a `?? 0` here would paint
// "0 products" over it.

import { useTranslation } from 'react-i18next';
import {
  Alert, Box, CircularProgress, Stack, Typography,
} from '@mui/material';
import PageBanner from '../common/PageBanner';

import ContactSupportButton from '../common/ContactSupportButton';
import EntryDialog from '../entry/EntryDialog';
import {
  CATALOG_PHASES, FAILURE_STATE, catalogImported, changedCount, failureState, isRefreshSync,
  ordersLanding as ordersLandingOf, phaseState, visitedCount,
} from '../../utils/syncGate';
import { useFormatters } from '../../hooks/useFormatters';
import { phaseNouns } from './phaseCopy';

const PHASE_COPY = {
  locations: {
    labelKey: 'syncScreen.phases.locations.label',
    progressKey: 'syncScreen.phases.locations.progress',
  },
  products: {
    labelKey: 'syncScreen.phases.products.label',
    progressKey: 'syncScreen.phases.products.progress',
  },
  inventory: {
    labelKey: 'syncScreen.phases.inventory.label',
    progressKey: 'syncScreen.phases.inventory.progress',
  },
};

// The phase's counters summed by one reader.
function total(phase, progress, read) {
  return phase.counters.reduce((sum, name) => sum + read(progress, name), 0);
}

function PhaseRow({ phase, state, progress, isRefresh }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const nouns = (read) => phaseNouns(t, formatNumber, phase, progress, read);

  let detail;
  if (state === 'pending') {
    detail = t('syncScreen.notStarted');
  } else if (isRefresh) {
    const outcome = total(phase, progress, changedCount) === 0
      ? t('syncScreen.allUpToDate')
      : t('syncScreen.updatedSuffix', { text: nouns(changedCount) });
    detail = `${t('syncScreen.checkedPrefix', { text: nouns(visitedCount) })} · ${outcome}`;
  } else {
    detail = nouns(visitedCount);
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center" data-testid={`phase-${phase.id}`} data-state={state}>
      <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
        {state === 'active' && <CircularProgress size={18} />}
        {state === 'completed' && <span aria-hidden="true">✓</span>}
        {state === 'pending' && <span aria-hidden="true">·</span>}
      </Box>
      <Box>
        <Typography fontWeight="var(--font-weight-semibold)">{t(PHASE_COPY[phase.id].labelKey)}</Typography>
        <Typography variant="body2" color="text.secondary">{detail}</Typography>
      </Box>
    </Stack>
  );
}

function FailureAlert({ failure, isRefresh, catalogIn, reconnectUrl }) {
  const { t } = useTranslation();
  if (failure === FAILURE_STATE.RECONNECT) {
    return (
      <PageBanner
        severity="warning"
        messages={[isRefresh ? t('syncScreen.refused.reconnectRefreshBody') : t('syncScreen.refused.reconnectImportBody')]}
        actions={[{ label: t('syncScreen.reconnect'), href: reconnectUrl }]}
      />
    );
  }
  if (failure === FAILURE_STATE.WAITING_FOR_SHOPIFY) {
    return (
      <Alert severity="info">
        {isRefresh ? t('syncScreen.refused.waitingRefreshBody') : t('syncScreen.refused.waitingImportBody')}
      </Alert>
    );
  }
  return (
    <Alert severity="error">
      <div>{failedBody(t, isRefresh, catalogIn)}</div>
      <div>
        {t('syncScreen.error.contactUs')}{' '}
        <ContactSupportButton />
      </div>
    </Alert>
  );
}

function failedBody(t, isRefresh, catalogIn) {
  if (isRefresh) return t('syncScreen.error.refreshBody');
  return catalogIn ? t('syncScreen.error.orderHistoryBody') : t('syncScreen.error.importBody');
}

function failedHeading(t, failure, isRefresh) {
  if (failure === FAILURE_STATE.RECONNECT) return t('syncScreen.heading.reconnect');
  if (failure === FAILURE_STATE.WAITING_FOR_SHOPIFY) return t('syncScreen.heading.waitingForShopify');
  return isRefresh ? t('syncScreen.heading.refreshFailed') : t('syncScreen.heading.importFailed');
}

export default function SyncScreen({ syncStatus, reconnectUrl }) {
  const { t } = useTranslation();
  const { sync } = syncStatus;
  const { status } = sync;
  const isRefresh = isRefreshSync(sync.syncType);
  const isFailed = status === 'failed';
  // Its orders, and the price, are still landing.
  const ordersLanding = ordersLandingOf(syncStatus);
  const failure = isFailed ? failureState(sync.failureReason) : null;

  let stateHeading;
  if (isFailed) stateHeading = failedHeading(t, failure, isRefresh);
  else if (ordersLanding) stateHeading = t('syncScreen.heading.loadingSalesHistory');
  else stateHeading = isRefresh ? t('syncScreen.heading.refreshing') : t('syncScreen.heading.importing');

  const activePhase = CATALOG_PHASES.find((p) => phaseState(p, sync) === 'active');
  const activeLabel = activePhase ? t(PHASE_COPY[activePhase.id].progressKey) : t('syncScreen.phases.generic');
  // A failed run is synchronizing nothing: its alert below says what happened.
  let body = null;
  if (ordersLanding) body = t('syncScreen.plan.ordersLandingBody');
  else if (!isFailed) {
    body = isRefresh
      ? t('syncScreen.checkingWhat', { what: activeLabel })
      : t('syncScreen.synchronizingWhat', { what: activeLabel });
  }

  return (
    <EntryDialog title={t('syncScreen.settingYourPlan')}>
      {/* The state is read out as it changes; the title bar above it does
          not change while the screen is up. */}
      <Stack spacing={2.5} role="status" aria-live="polite">
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!isFailed && <CircularProgress size={22} />}
          <Typography variant="h5" component="h3">{stateHeading}</Typography>
        </Stack>
        {!isFailed && (
          <Typography color="text.secondary">{t('syncScreen.plan.body')}</Typography>
        )}
        {body && <Typography color="text.secondary">{body}</Typography>}

        <Stack spacing={1.5}>
          {CATALOG_PHASES.map((phase) => (
            <PhaseRow
              key={phase.id}
              phase={phase}
              state={phaseState(phase, sync)}
              progress={sync.progress}
              isRefresh={isRefresh}
            />
          ))}
        </Stack>

        {isFailed && (
          <FailureAlert
            failure={failure}
            isRefresh={isRefresh}
            catalogIn={catalogImported(sync)}
            reconnectUrl={reconnectUrl}
          />
        )}
      </Stack>
    </EntryDialog>
  );
}
